import {
  addDays,
  differenceInCalendarDays,
  eachDayOfInterval,
  endOfMonth,
  format,
  isSameDay,
  isValid,
  max,
  min,
  parseISO,
  startOfMonth,
} from "date-fns";
import type { Locale } from "date-fns";
import { enUS, lt } from "date-fns/locale";
import { MAX_LEAVE_DAYS } from "./leaveCalendar.constants";

export function leaveCalendarDateLocale(lang?: string): Locale {
  return lang === "lt" ? lt : enUS;
}

export function monthDayInterval(year: number, monthIndex: number) {
  const anchor = new Date(year, monthIndex, 1);
  const start = startOfMonth(anchor);
  const end = endOfMonth(anchor);
  return { start, end, days: eachDayOfInterval({ start, end }) };
}

export function yearDayInterval(year: number) {
  const start = new Date(year, 0, 1);
  const end = new Date(year, 11, 31);
  return { start, end, days: eachDayOfInterval({ start, end }) };
}

export function dayKey(d: Date): string {
  return format(d, "yyyy-MM-dd");
}

export function isDateOnHoliday(
  d: Date,
  holidays: { date: string }[]
): boolean {
  const key = dayKey(d);
  return holidays.some((h) => h.date === key);
}

export function isToday(d: Date): boolean {
  return isSameDay(d, new Date());
}

export function parseDayKey(key: string): Date {
  return parseISO(key);
}

export function clampDateToMonth(
  d: Date,
  year: number,
  monthIndex: number
): Date {
  const first = new Date(year, monthIndex, 1);
  const last = endOfMonth(first);
  if (d < first) return first;
  if (d > last) return last;
  return d;
}

export function formatHeaderDay(d: Date, locale: Locale): {
  dow: string;
  dom: string;
  monthHint: string;
} {
  return {
    dow: format(d, "EEE", { locale }),
    dom: format(d, "d", { locale }),
    monthHint: format(d, "MMM", { locale }),
  };
}

function normalizeLeaveBounds(startDate: string, endDate: string): { start: Date; end: Date } | null {
  const rawStart = parseISO(startDate);
  const rawEnd = parseISO(endDate);
  if (!isValid(rawStart) || !isValid(rawEnd)) return null;
  return rawStart <= rawEnd
    ? { start: rawStart, end: rawEnd }
    : { start: rawEnd, end: rawStart };
}

export function leaveIntersectsMonth(
  startDate: string,
  endDate: string,
  monthStart: Date,
  monthEnd: Date
): boolean {
  const bounds = normalizeLeaveBounds(startDate, endDate);
  if (!bounds) return false;
  return bounds.start <= monthEnd && bounds.end >= monthStart;
}

/** Days of a leave clipped to a calendar month; safe when dates are invalid or inverted. */
export function leaveDaysInMonth(
  startDate: string,
  endDate: string,
  monthStart: Date,
  monthEnd: Date
): Date[] {
  const bounds = normalizeLeaveBounds(startDate, endDate);
  if (!bounds) return [];
  const s = max([bounds.start, monthStart]);
  const e = min([bounds.end, monthEnd]);
  if (!isValid(s) || !isValid(e) || s > e) return [];
  return eachDayOfInterval({ start: s, end: e });
}

/** Inclusive calendar-day span of a leave range; null when either key is empty/invalid. */
export function leaveRangeDayCount(
  startKey: string,
  endKey: string
): number | null {
  if (!startKey || !endKey) return null;
  const start = parseISO(startKey);
  const end = parseISO(endKey);
  if (!isValid(start) || !isValid(end)) return null;
  return differenceInCalendarDays(end, start) + 1;
}

/** True when a leave range exceeds `maxDays` (inclusive count); false when dates are missing/invalid. */
export function isLeaveRangeTooLong(
  startKey: string,
  endKey: string,
  maxDays: number = MAX_LEAVE_DAYS
): boolean {
  const count = leaveRangeDayCount(startKey, endKey);
  if (count === null) return false;
  return count > maxDays;
}

/** Latest end date (local midnight) still within `maxDays` of `startKey`; null when invalid. */
export function maxLeaveEndDate(
  startKey: string,
  maxDays: number = MAX_LEAVE_DAYS
): Date | null {
  if (!startKey) return null;
  const start = parseISO(startKey);
  if (!isValid(start)) return null;
  return addDays(start, maxDays - 1);
}
